import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { useLanguage } from "@/hooks/useLanguage";

export const NewsletterSubscribe = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // Note: emailSchema validation messages will use current language
  const getEmailSchema = () => z.object({
    email: z.string().email(t('footer.invalidEmail')).max(255, t('footer.emailTooLong')),
  });

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const validated = getEmailSchema().parse({ email: email.trim() });
      setIsLoading(true);

      // Debug + visible UX feedback
      console.log("[Newsletter] Invoking send-newsletter-welcome", validated.email);

      const { data, error } = await supabase.functions.invoke(
        "send-newsletter-welcome",
        {
          body: { email: validated.email },
        },
      );

      console.log("[Newsletter] Function response", { data, error });

      if (error) {
        throw new Error(error.message || t('footer.subscribeError'));
      }

      if ((data as any)?.error) {
        throw new Error((data as any).error);
      }

      toast.success((data as any)?.message || t('footer.subscribeSuccess'));
      setEmail("");
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.issues[0].message);
      } else {
        console.error("[Newsletter] Subscribe error:", error);
        toast.error(error.message || t('footer.subscribeError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <h3 className="text-lg font-semibold mb-4 text-muted-foreground">{t('footer.newsletterTitle')}</h3>
      <form onSubmit={handleSubscribe} className="flex gap-2">
        <Input
          id="newsletter-email"
          name="email"
          type="email"
          placeholder={t('footer.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
          autoComplete="email"
          className="flex-1 bg-background/50 border-border"
        />
        <Button 
          type="submit" 
          size="icon"
          aria-label={t('footer.subscribeButton')}
          title={t('footer.subscribeButton')}
          disabled={isLoading}
          className="bg-primary hover:bg-primary/90"
        >
          <Mail className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};
